import { Component } from '@theme/component';

const forms = new Set(['REZMVY', 'RzXgt6', 'StPkba', 'XeUs9M', 'SzD6Bg', 'XLeujL']);
const dismissedKey = 'owo_shipping_dismissed';
const completedKey = 'owo_shipping_completed';
const positionKey = 'owo_shipping_position';

class FreeShippingTeaser extends Component {
  #abort;
  #dismissed = false;
  #completed = false;
  #open = false;
  #drag;
  #suppressClick = false;
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    window.addEventListener('klaviyoForms', this.#formEvent, options);
    window.addEventListener('resize', this.#restorePosition, options);
    window.visualViewport?.addEventListener('resize', this.#restorePosition, options);
    window.addEventListener('pageshow', this.#refresh, options);
    window.addEventListener('storage', this.#refresh, options);
    this.addEventListener('pointerdown', this.#pointerDown, options);
    this.addEventListener('pointermove', this.#pointerMove, options);
    this.addEventListener('pointerup', this.#pointerUp, options);
    this.addEventListener('pointercancel', this.#pointerUp, options);
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    clearTimeout(this.#timer);
    this.#drag = undefined;
  }

  #read(key, persistent = false) {
    try { return (persistent ? localStorage : sessionStorage).getItem(key); } catch { return null; }
  }

  #write(key, value, persistent = false) {
    try { (persistent ? localStorage : sessionStorage).setItem(key, value); } catch { /* Private storage may be unavailable. */ }
  }

  #refresh = () => {
    const cookieDismissed = document.cookie.split('; ').some((entry) => entry === `${dismissedKey}=1`);
    this.hidden = this.#open || this.#dismissed || cookieDismissed || this.#completed || this.#read(completedKey, true) === '1';
    if (!this.hidden) this.#restorePosition();
  };

  dismiss() {
    this.#dismissed = true;
    document.cookie = `${dismissedKey}=1; Path=/; SameSite=Lax; Secure`;
    this.#refresh();
  }

  openOffer() {
    if (this.#suppressClick) { this.#suppressClick = false; return; }
    // Keep the trigger visible until Klaviyo confirms that the popup really opened.
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', this.dataset.formId, () => {
      this.#open = true;
      this.#refresh();
      window.essentialCartApi?.closeCart?.();
    }]);
  }

  #formEvent = (event) => {
    if (!forms.has(event.detail?.formId)) return;
    const type = event.detail.type;
    if (type === 'open') this.#open = true;
    // Closing the form brings the teaser back unless signup succeeded or the
    // visitor explicitly dismissed the teaser itself for this session.
    if (type === 'close') this.#open = false;
    // Klaviyo's conversion event confirms signup, even if an optional SMS step follows.
    // Do not treat arbitrary stepSubmit events as successful subscriptions.
    if (type === 'submit') {
      this.#completed = true;
      this.#write(completedKey, '1', true);
    }
    this.#refresh();
  };

  #bounds() {
    const viewport = window.visualViewport;
    return { left: (viewport?.offsetLeft || 0) + 12, top: (viewport?.offsetTop || 0) + 12,
      width: viewport?.width || window.innerWidth, height: viewport?.height || window.innerHeight };
  }

  #place(x, y) {
    const b = this.#bounds();
    const left = Math.max(b.left, Math.min(x, b.left + b.width - this.offsetWidth - 24));
    const top = Math.max(b.top, Math.min(y, b.top + b.height - this.offsetHeight - 24));
    this.style.insetInlineStart = 'auto';
    this.style.right = 'auto';
    this.style.left = `${left}px`;
    this.style.top = `${top}px`;
    this.style.bottom = 'auto';
    return { x: left, y: top };
  }

  #restorePosition = () => {
    if (this.hidden) return;
    if (!window.matchMedia('(max-width: 749px)').matches) { this.removeAttribute('style'); return; }
    try {
      const position = JSON.parse(this.#read(positionKey) || 'null');
      if (position && Number.isFinite(position.x) && Number.isFinite(position.y)) this.#place(position.x, position.y);
    } catch { /* Ignore invalid stored positions. */ }
  };

  #pointerDown = (event) => {
    if (!event.isPrimary || event.button !== 0 || !window.matchMedia('(max-width: 749px)').matches ||
        !(event.target instanceof Element) || event.target.closest('.owo-shipping-teaser__close')) return;
    const rect = this.getBoundingClientRect();
    this.#drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top, moved: false };
    this.#suppressClick = false;
  };

  #pointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || drag.id !== event.pointerId) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved && Math.hypot(dx, dy) < 8) return;
    drag.moved = true;
    this.setPointerCapture(event.pointerId);
    this.#place(drag.left + dx, drag.top + dy);
  };

  #pointerUp = (event) => {
    const drag = this.#drag;
    if (!drag || drag.id !== event.pointerId) return;
    if (drag.moved) {
      this.#suppressClick = true;
      const rect = this.getBoundingClientRect();
      this.#write(positionKey, JSON.stringify({ x: rect.left, y: rect.top }));
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => { this.#suppressClick = false; }, 400);
    }
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    this.#drag = undefined;
  };
}

if (!customElements.get('free-shipping-teaser')) customElements.define('free-shipping-teaser', FreeShippingTeaser);
